import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { AlertTriangle, User, Phone, Calendar, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const QRPublicPage = () => {
  const { qrCodeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [qrCodeId]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API}/qr/public/${qrCodeId}`);
      if (response.ok) {
        setData(await response.json());
      } else {
        setError('Invalid QR code or horse not found');
      }
    } catch (error) {
      setError('Failed to load information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-forest/10 to-bone flex items-center justify-center p-4">
        <div className="animate-spin w-10 h-10 border-4 border-forest border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-forest/10 to-bone flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-xl font-bold mb-2">QR Code Error</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-forest/10 to-bone p-4" data-testid="qr-public-page">
      <div className="max-w-lg mx-auto space-y-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4">
            <img src="/logo.png" alt="EquiVault" className="h-7 w-auto" />
            <span className="font-serif font-bold text-forest">EquiVault</span>
          </div>
          <h1 className="text-2xl font-bold font-serif">Horse ID Profile</h1>
        </div>

        {/* Horse Info Card */}
        <Card className="border-0 shadow-lg bg-white overflow-hidden">
          <div className="bg-forest text-white p-4">
            <h2 className="text-xl font-bold">{data.horse.name}</h2>
          </div>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground block text-xs uppercase tracking-wide">Breed</span>
                <span className="font-medium">{data.horse.breed || '-'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs uppercase tracking-wide">Sex</span>
                <span className="font-medium">{data.horse.sex || '-'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs uppercase tracking-wide">Colour</span>
                <span className="font-medium">{data.horse.colour || '-'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs uppercase tracking-wide">DOB</span>
                <span className="font-medium">{data.horse.dob || '-'}</span>
              </div>
              {data.horse.microchip && (
                <div className="col-span-2">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wide">Microchip</span>
                  <span className="font-medium font-mono text-xs">{data.horse.microchip}</span>
                </div>
              )}
              {(data.horse.registration_body || data.horse.registration_number) && (
                <div className="col-span-2">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wide">Registration</span>
                  <span className="font-medium">
                    {data.horse.registration_body && `${data.horse.registration_body}: `}{data.horse.registration_number || '-'}
                  </span>
                </div>
              )}
            </div>
            <div className="pt-2 border-t">
              <span className="text-muted-foreground block text-xs uppercase tracking-wide">Owner</span>
              <span className="font-medium">{data.horse.owner || '-'}</span>
            </div>
          </CardContent>
        </Card>

        {/* First Contact Card */}
        {(data.first_contact.name || data.first_contact.phone) && (
          <Card className="border-0 shadow-lg bg-white border-l-4 border-l-forest">
            <CardContent className="p-4">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <Phone className="w-5 h-5 text-forest" />
                First Contact
              </h3>
              <div className="space-y-2">
                {data.first_contact.name && (
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-lg">{data.first_contact.name}</span>
                  </div>
                )}
                {data.first_contact.phone && (
                  <a 
                    href={`tel:${data.first_contact.phone}`}
                    className="flex items-center gap-3 text-forest hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="font-medium text-lg">{data.first_contact.phone}</span>
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rider Info Card */}
        {data.rider && (
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-4">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-saddle" />
                Rider Profile
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wide">Name</span>
                  <p className="font-medium text-lg">{data.rider.name}</p>
                </div>
                
                {data.rider.contact_number && (
                  <a 
                    href={`tel:${data.rider.contact_number}`}
                    className="flex items-center gap-2 text-forest hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{data.rider.contact_number}</span>
                  </a>
                )}
                
                {data.rider.birth_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">DOB: {data.rider.birth_date}</span>
                  </div>
                )}

                {(data.rider.emergency_contact_name || data.rider.emergency_contact_number) && (
                  <div className="bg-destructive/5 p-3 rounded-lg border border-destructive/20">
                    <p className="flex items-center gap-1 text-destructive font-bold mb-2 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      Emergency Contact
                    </p>
                    {data.rider.emergency_contact_name && (
                      <p className="font-medium">{data.rider.emergency_contact_name}</p>
                    )}
                    {data.rider.emergency_contact_number && (
                      <a 
                        href={`tel:${data.rider.emergency_contact_number}`}
                        className="text-forest hover:underline"
                      >
                        {data.rider.emergency_contact_number}
                      </a>
                    )}
                  </div>
                )}

                {data.rider.emergency_medical_notes && (
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="flex items-center gap-1 text-amber-700 font-bold mb-2 text-sm">
                      <Heart className="w-4 h-4" />
                      Medical Notes
                    </p>
                    <p className="text-sm text-amber-800">{data.rider.emergency_medical_notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground pt-4">
          <p>Powered by EquiVault</p>
        </div>
      </div>
    </div>
  );
};
